<script setup lang="ts">
import { ref, unref, onBeforeUnmount } from 'vue'
import { useEditor } from '@tiptap/vue-3'
import { generateJSON } from '@tiptap/html'
import DOMPurify from 'dompurify'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Paragraph from '@tiptap/extension-paragraph'
import data from 'emoji-mart-vue-fast/data/all.json'
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast/src'
import type { Profile } from '~/types/profile'
import { Emoji } from '~/extensions/emoji'
import 'emoji-mart-vue-fast/css/emoji-mart.css'
import Image from '@tiptap/extension-image'
import Dropcursor from '@tiptap/extension-dropcursor'
import { Video } from '~/extensions/video'
import { type ToastNotification } from '~/components/Toast.vue'
import SkeletonLoader from '~/components/SkeletonLoader.vue'
import Modal from '~/components/Common/Modal/Modal.vue'
import Button from '~/components/Common/Button/Button.vue'

const props = defineProps<{
	content: string
	hideToolbar?: boolean
	enableHtml?: boolean // Controls visibility of HTML import/export buttons
	enableVideo?: boolean // Controls video upload functionality
	background?: string
	placeholder?: string
	cdn_user_id?: number // User ID for CDN uploads
}>()

const emit = defineEmits(['input'])

const profile = inject('profile') as Ref<Profile>
const toast = inject('toast') as Ref<ToastNotification>

const showEmojiPicker = ref(false)
const error = ref<string | null>(null)
const loading = ref(false)
let emojiIndex = new EmojiIndex(data)

const localContent = ref(props.content)

const editor = useEditor({
	content: localContent.value,
	extensions: [
		StarterKit,
		Emoji,
		Placeholder.configure({
			placeholder: props.placeholder ?? 'Write something …',
			emptyEditorClass:
				'before:content-[attr(data-placeholder)] before:absolute before:opacity-50 before-pointer-events-none',
		}),
		Paragraph.configure({
			HTMLAttributes: {
				class: 'text-base',
			},
		}),
		Image,
		Dropcursor,
		...(props.enableVideo ? [Video] : []),
	],
	editorProps: {
		attributes: {
			class:
				'py-4 px-4 prose prose-sm sm:prose-base lg:prose-lg xl:prose-2xl focus:outline-none min-h-48 ' +
				(props.background ?? 'bg-green-50'),
		},
	},
	onUpdate: () => {
		emit('input', editor.value?.getHTML())
	},
})

const toggleEmojiPicker = () => {
	showEmojiPicker.value = !showEmojiPicker.value
}

const onEmojiSelect = (emoji: any) => {
	editor.value?.commands.insertEmoji(emoji.native)
	showEmojiPicker.value = false
}

const imageUploader = () => {
	const input = document.createElement('input')
	input.type = 'file'
	input.accept = 'image/*'
	input.onchange = async event => {
		const files = (event.target as HTMLInputElement).files
		if (files && files.length > 0) {
			const fileReader = new FileReader()
			fileReader.readAsDataURL(files[0])
			fileReader.onload = async () => {
				if (typeof fileReader.result === 'string') {
					try {
						loading.value = true
						const result = await fetch('https://n8n.healthnutrition.com/webhook/image/upload', {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify({
								user_id: props.cdn_user_id || profile.value?.id,
								skip_resize: true,
								image: fileReader.result,
							}),
						})

						const response = <{ url?: string; error?: boolean }>await result.json()

						if (response.error || !response.url) {
							toast.value = {
								show: true,
								timeout: 10,
								title: 'Error',
								message: 'Error uploading image. Please try again.',
								type: 'error',
							}
						} else {
							error.value = null
							unref(editor).chain().focus().setImage({ src: response.url }).run()
						}
					} catch (e) {
						console.error(e)
						toast.value = {
							show: true,
							timeout: 10,
							title: 'Error',
							message: 'Error uploading image. Please try again.',
							type: 'error',
						}
					} finally {
						loading.value = false
					}
				}
			}
		}
}
input.click()
}

const videoUploader = () => {
	const url = prompt('Enter video URL:')
	if (url) {
		try {
			loading.value = true
			// Determine video type based on URL extension
			const videoType = url.toLowerCase().endsWith('.mp4')
				? 'video/mp4'
				: url.toLowerCase().endsWith('.webm')
					? 'video/webm'
					: url.toLowerCase().endsWith('.ogg')
						? 'video/ogg'
						: 'video/mp4' // Default to mp4 if unknown

			unref(editor).chain().focus().setVideo({ src: url, type: videoType }).run()
			loading.value = false
		} catch (e) {
			console.error(e)
			toast.value = {
				show: true,
				timeout: 10,
				title: 'Error',
				message: 'Error embedding video. Please try again.',
				type: 'error',
			}
			loading.value = false
		}
	}
}

const exportHTML = () => {
	try {
		const html = editor.value?.getHTML() ?? ''
		navigator.clipboard
			.writeText(html)
			.then(() => {
				toast.value = {
					show: true,
					timeout: 10,
					title: `Copied to clipboard`,
					message: `HTML copied to clipboard successfully.`,
					type: 'success',
				}
			})
			.catch(e => {
				console.error('Error copying to clipboard:', e)
				toast.value = {
					show: true,
					timeout: 10,
					title: `Error copying to clipboard`,
					message: `Failed to copy HTML to clipboard.`,
					type: 'error',
				}
			})
	} catch (e) {
		console.error('Error exporting HTML:', e)
		toast.value = {
			show: true,
			timeout: 10,
			title: `Error copying to clipboard`,
			message: `Failed to copy HTML to clipboard.`,
			type: 'error',
		}
	}
}

const showImportModal = ref(false)
const importHtmlContent = ref('')

const importHTML = () => {
	showImportModal.value = true
}

const handleImportConfirm = async () => {
	try {
		if (!importHtmlContent.value) return

		// Sanitize HTML before converting to JSON
		const cleanHtml = DOMPurify.sanitize(importHtmlContent.value, {
			ALLOWED_TAGS: ['p', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'video', 'source'],
			ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'type', 'controls', 'width'],
		})

		const doc = generateJSON(cleanHtml, [
			StarterKit,
			Emoji,
			Placeholder.configure({
				placeholder: 'Write something …',
				emptyEditorClass:
					'before:content-[attr(data-placeholder)] before:absolute before:opacity-50 before-pointer-events-none',
			}),
			Paragraph.configure({
				HTMLAttributes: {
					class: 'text-base',
				},
			}),
			Image,
			Dropcursor,
			...(props.enableVideo ? [Video] : []),
		])
		editor.value?.commands.setContent(doc)
		showImportModal.value = false
		importHtmlContent.value = ''
		emit('input', editor.value?.getHTML())
	} catch (error) {
		console.error('Error importing HTML:', error)
		toast.value = {
			show: true,
			timeout: 10,
			title: `Error importing HTML`,
			message: `Failed to import HTML. Check the console for details.`,
			type: 'error',
		}
	}
}

onBeforeUnmount(() => {
	unref(editor).destroy()
})
</script>

<template>
	<div :class="background ?? 'bg-green-50'">
		<div
			v-if="editor && !hideToolbar"
			class="flex flex-row gap-2 flex-wrap p-1"
			:class="background ?? 'bg-green-50'">
			<div class="flex flex-row gap-2 flex-wrap p-1" :class="background ?? 'bg-green-50'">
				<Icon
					name="ph:arrow-u-down-left"
					class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
					:disabled="!editor.can().chain().focus().undo().run()"
					@click="editor.chain().focus().undo().run()" />
				<Icon
					name="ph:arrow-u-down-right"
					class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
					:disabled="!editor.can().chain().focus().redo().run()"
					@click="editor.chain().focus().redo().run()" />
				<Icon
					name="ph:text-b"
					class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
					:class="{ 'is-active': editor.isActive('bold') }"
					:disabled="!editor.can().chain().focus().toggleBold().run()"
					@click="editor.chain().focus().toggleBold().run()" />
				<Icon
					name="ph:text-italic"
					class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
					:class="{ 'is-active': editor.isActive('italic') }"
					:disabled="!editor.can().chain().focus().toggleItalic().run()"
					@click="editor.chain().focus().toggleItalic().run()" />
				<Icon
					name="ph:paragraph"
					class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
					:class="{ 'is-active': editor.isActive('paragraph') }"
					:disabled="!editor.can().chain().focus().setParagraph().run()"
					@click="editor.chain().focus().setParagraph().run()" />
				<!-- <Icon
				name="ph:text-h-two"
				class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
				:class="{ 'is-active': editor.isActive('heading') }"
				:disabled="!editor.can().chain().focus().toggleHeading({ level: 2 }).run()"
				@click="editor.chain().focus().toggleHeading({ level: 2 }).run()" />
			<Icon
				name="ph:text-h-three"
				class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
				:class="{ 'is-active': editor.isActive('heading') }"
				:disabled="!editor.can().chain().focus().toggleHeading({ level: 3 }).run()"
				@click="editor.chain().focus().toggleHeading({ level: 3 }).run()" />
			<Icon
				name="ph:text-h-four"
				class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
				:class="{ 'is-active': editor.isActive('heading') }"
				:disabled="!editor.can().chain().focus().toggleHeading({ level: 4 }).run()"
				@click="editor.chain().focus().toggleHeading({ level: 4 }).run()" /> -->
				<!-- <Icon
				name="ph:list-bullets"
				class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
				:class="{ 'is-active': editor.isActive('bulletList') }"
				@click="editor.chain().focus().toggleBulletList().run()" />
			<Icon
				name="ph:list-numbers"
				class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
				:class="{ 'is-active': editor.isActive('orderedList') }"
				@click="editor.chain().focus().toggleOrderedList().run()" />
			<Icon
				name="ph:quotes"
				class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
				:class="{ 'is-active': editor.isActive('blockquote') }"
				:disabled="!editor.can().chain().focus().toggleBlockquote().run()"
				@click="editor.chain().focus().toggleBlockquote().run()" /> -->
				<!-- <Icon
				name="ph:minus"
				class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
				:class="{ 'is-active': editor.isActive('horizontalRule') }"
				:disabled="!editor.can().chain().focus().setHorizontalRule().run()"
				@click="editor.chain().focus().setHorizontalRule().run()" /> -->
				<Icon
					name="ph:smiley"
					class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
					:class="{ 'is-active': showEmojiPicker }"
					@click="toggleEmojiPicker" />
				<Icon
					name="ph:image"
					class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
					@click="imageUploader" />
				<Icon
					v-if="enableVideo"
					name="ph:video-camera"
					class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
					@click="videoUploader" />
				<!-- HTML Import/Export buttons -->
				<template v-if="enableHtml">
					<Icon
						name="ph:file-html"
						class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
						@click="exportHTML"
						title="Export HTML" />
					<Icon
						name="ph:file-arrow-up"
						class="w-5 h-5 sm:w-6 sm:h-6 cursor-pointer hover:text-green-600"
						@click="importHTML"
						title="Import HTML" />
				</template>
			</div>

			<div v-if="showEmojiPicker" class="relative">
				<div class="absolute z-10 bg-white shadow-lg rounded-lg p-2 border border-gray-200">
					<Picker
						:data="emojiIndex"
						title="Pick an emoji"
						emoji="point_up"
						:style="{ width: '320px' }"
						@select="onEmojiSelect"
						color="#00a600"
						:showPreview="false" />
				</div>
			</div>
		</div>

		<SkeletonLoader v-if="loading" class="pt-4 mb-4 w-full h-12" />

		<TiptapEditorContent v-else :editor="editor" class="pt-4 tiptap-editor-content" />

		<!-- HTML Import Modal -->

		<Modal
			:show="showImportModal"
			title="Import HTML"
			@close="
				() => {
					showImportModal = false
					importHtmlContent = ''
				}
			"
		>
		<div class="space-y-4">
					<div>
						<label class="block text-sm font-medium text-gray-700">Paste HTML Content</label>
						<textarea
							v-model="importHtmlContent"
							rows="10"
							class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
							placeholder="Paste your HTML content here..."></textarea>
					</div>
					<div class="mt-6 flex justify-between items-center">
						<Button
							@click="
								() => {
									showImportModal = false
									importHtmlContent = ''
								}
							"
							>Cancel</Button
						>
						<Button @click="handleImportConfirm">Import</Button>
					</div>
				</div>
		</Modal>

	</div>
</template>
<style>
.emoji-mart button {
	background-color: transparent;
	width: unset !important;
	height: unset !important;
}

</style>
